import type { AreaUnit, OrgMembership } from './types'

// The server stores areas in square metres; the app shows (and takes) acres or hectares.
const M2_PER_ACRE = 4046.8564224
const M2_PER_HECTARE = 10_000

/** The unit the org wants areas shown in: acres for imperial, hectares for metric. */
export function orgAreaUnit(org: Pick<OrgMembership, 'unitSystem'> | null | undefined): AreaUnit {
  return org?.unitSystem === 'METRIC' ? 'hectare' : 'acre'
}

export const areaUnitLabel = (unit: AreaUnit) => (unit === 'acre' ? 'ac' : 'ha')

/** How the API names an area unit when we send `{ value, unit }`. */
export const apiAreaUnit = (unit: AreaUnit) => (unit === 'acre' ? 'acre' : 'ha')

export function toM2(value: number, unit: AreaUnit) {
  return value * (unit === 'acre' ? M2_PER_ACRE : M2_PER_HECTARE)
}

export function fromM2(areaM2: number, unit: AreaUnit) {
  return areaM2 / (unit === 'acre' ? M2_PER_ACRE : M2_PER_HECTARE)
}

/** `12.5 ac` / `5.1 ha`. */
export function formatArea(areaM2: number, unit: AreaUnit) {
  return `${fromM2(areaM2, unit).toFixed(1)} ${areaUnitLabel(unit)}`
}

/** A number for an input box: up to 3 decimals, no trailing zeros ("12.5", not "12.500"). */
export function areaInputValue(areaM2: number, unit: AreaUnit) {
  return String(Number(fromM2(areaM2, unit).toFixed(3)))
}

/** The request body for an area typed by the user, in the unit they chose. */
export const areaBody = (value: number, unit: AreaUnit) => ({ value, unit: apiAreaUnit(unit) })
